/**
 * Post-signup redirect to the CRISP thank-you page.
 *
 * The signup app normally runs inside an iframe on the WordPress site, so
 * redirecting `window.location` would only swap the iframe's contents and
 * leave the customer looking at a thank-you page framed inside the signup
 * page. We need the WHOLE page to navigate.
 *
 * Two paths, in order of preference:
 *
 *  1. postMessage to the parent — the embed script (crisp-address-widget.js)
 *     listens for `crisp-signup-redirect` and sets `window.location` on the
 *     top-level page. This is the only approach that works when the iframe
 *     and the parent are on different origins (the app is served from
 *     www.crispwireless.com.au while the WP page may be on the bare domain),
 *     because cross-origin access to `window.top.location` is blocked.
 *
 *  2. Direct `window.top.location` assignment — a fallback for when the
 *     parent page has an older copy of the embed script that doesn't know
 *     about the redirect message. Only succeeds same-origin; the try/catch
 *     swallows the cross-origin SecurityError.
 *
 * If both fail the customer stays on the in-app confirmation screen, which
 * still shows their reference number — a degraded but not broken outcome.
 */

export const THANK_YOU_URL =
  'https://crispwireless.com.au/thank-you/?k=hQ7mK2vRx9TpLw4ZbN6sYc3D';

/** How long the confirmation screen shows before the redirect fires (ms). */
export const THANK_YOU_REDIRECT_DELAY = 1200;

/**
 * Navigate the top-level window to the thank-you page, breaking out of the
 * iframe if we're in one.
 */
export function redirectToThankYou(url: string = THANK_YOU_URL): void {
  if (typeof window === 'undefined') return;

  const inIframe = window.parent !== window;

  if (inIframe) {
    // Preferred: ask the parent page to navigate itself.
    try {
      window.parent.postMessage({ type: 'crisp-signup-redirect', url }, '*');
    } catch (err) {
      // Ignore — fall through to the direct attempt below.
    }

    // Fallback for parents running an older embed script. Same-origin only;
    // throws SecurityError cross-origin, which we deliberately ignore.
    try {
      if (window.top) {
        window.top.location.href = url;
      }
    } catch (err) {
      // Cross-origin: the postMessage above is our only route.
    }

    return;
  }

  // Not embedded — the app is the top-level page, so navigate directly.
  window.location.href = url;
}
